import { useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Notice } from '../components/Notice';
import {
  downloadJsonBackup,
  downloadTrialsCsv,
  inspectBackupJson,
  type BackupPreview,
} from '../data';

interface PendingBackup {
  filename: string;
  json: string;
  preview: BackupPreview;
}

interface PageNotice {
  title: string;
  body: string;
  tone?: 'info' | 'warning' | 'danger';
}

export function SettingsPage() {
  const app = useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [profileName, setProfileName] = useState(app.settings.profileName);
  const [age, setAge] = useState(String(app.settings.age));
  const [notice, setNotice] = useState<PageNotice>();
  const [pendingBackup, setPendingBackup] = useState<PendingBackup>();

  const saveProfile = async () => {
    const normalizedName = profileName.trim();
    const normalizedAge = Number(age);
    if (!normalizedName) {
      setNotice({
        title: '表示名を入力してください。',
        body: '1〜24文字で入力します。',
        tone: 'danger',
      });
      return;
    }
    if (!Number.isInteger(normalizedAge) || normalizedAge < 18 || normalizedAge > 99) {
      setNotice({
        title: '年齢を確認してください。',
        body: '18〜99の整数で入力します。',
        tone: 'danger',
      });
      return;
    }
    try {
      await app.updateSettings({ profileName: normalizedName, age: normalizedAge });
      setProfileName(normalizedName);
      setAge(String(normalizedAge));
      setNotice({ title: 'プロフィールを保存しました。', body: '端末内の設定を更新しました。' });
    } catch {
      setNotice({
        title: 'プロフィールを保存できませんでした。',
        body: 'ブラウザの空き容量とサイトデータ保存設定を確認してください。',
        tone: 'danger',
      });
    }
  };

  const importJson = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const json = await file.text();
      setPendingBackup({ filename: file.name, json, preview: inspectBackupJson(json) });
      setNotice(undefined);
    } catch {
      setPendingBackup(undefined);
      setNotice({
        title: 'このファイルは復元できません。',
        body: 'ファイル形式、日時、セッションと試行の整合性を確認してください。',
        tone: 'danger',
      });
    } finally {
      event.target.value = '';
    }
  };

  const confirmImport = async () => {
    if (!pendingBackup) return;
    const confirmed = window.confirm(
      '現在の端末内データを、選択したバックアップで置き換えます。元に戻せません。続行しますか？',
    );
    if (!confirmed) return;
    try {
      await app.importBackup(pendingBackup.json);
      setProfileName(pendingBackup.preview.profileName ?? 'PLAYER 01');
      setAge(String(pendingBackup.preview.age ?? 34));
      setPendingBackup(undefined);
      setNotice({ title: 'バックアップを復元しました。', body: '検証済みのデータで端末内記録を置き換えました。' });
    } catch {
      setNotice({
        title: 'バックアップを復元できませんでした。',
        body: 'ブラウザの空き容量とサイトデータ保存設定を確認してください。',
        tone: 'danger',
      });
    }
  };

  const clear = async () => {
    if (!window.confirm('設定とすべての記録を削除します。元に戻せません。')) return;
    try {
      await app.clearAll();
      setProfileName('PLAYER 01');
      setAge('34');
      setPendingBackup(undefined);
      setNotice({ title: 'このブラウザ内の記録を削除しました。', body: '画面設定、実施記録、履歴を削除し、個人設定を初期値に戻しました。' });
    } catch {
      setNotice({
        title: '端末内データを削除できませんでした。',
        body: 'ブラウザのサイトデータ設定を確認し、もう一度実行してください。',
        tone: 'danger',
      });
    }
  };

  const exportData = async (kind: 'json' | 'csv') => {
    try {
      if (kind === 'json') await downloadJsonBackup();
      else await downloadTrialsCsv();
      setNotice({
        title: kind === 'json' ? 'バックアップを保存しました。' : '詳しいデータを保存しました。',
        body: 'ブラウザの保存先を確認してください。',
      });
    } catch {
      setNotice({
        title: 'データを書き出せませんでした。',
        body: '端末内データの読込権限とブラウザのダウンロード設定を確認してください。',
        tone: 'danger',
      });
    }
  };

  return (
    <div className="page">
      <header className="page-heading"><div><div className="eyebrow">設定</div><h1>プロフィールと記録の管理</h1><p>記録はこのブラウザ内だけに保存され、インターネット上には送信されません。</p></div></header>
      {notice && <Notice title={notice.title} tone={notice.tone}>{notice.body}</Notice>}
      <div className="grid-2 settings-grid">
        <section className="panel">
          <div className="panel-label">プロフィール</div><h2>個人設定</h2>
          <div className="form-grid settings-form"><div className="field"><label htmlFor="profile-name">表示名</label><input id="profile-name" value={profileName} maxLength={24} onChange={(event) => setProfileName(event.target.value)} /></div><div className="field"><label htmlFor="profile-age">年齢</label><input id="profile-age" type="number" min="18" max="99" step="1" value={age} onChange={(event) => setAge(event.target.value)} /></div></div>
          <button className="button" type="button" onClick={saveProfile}>設定を保存</button>
        </section>
        <section className="panel">
          <div className="panel-label">画面と見る位置</div><h2>現在の設定</h2>
          {app.calibration && app.environment ? <dl className="settings-definition"><div><dt>画面上の1センチ</dt><dd>{app.calibration.pixelsPerCentimeter.toFixed(2)}ピクセル</dd></div><div><dt>見る距離</dt><dd>{app.calibration.viewingDistanceCm}センチ</dd></div><div><dt>画面の更新回数</dt><dd>毎秒約{Math.round(app.environment.refreshRateHz)}回</dd></div><div><dt>今回の状態</dt><dd>{app.calibration.correction === 'none' ? '裸眼' : app.calibration.correction === 'glasses' ? '眼鏡' : 'コンタクトレンズ'}</dd></div></dl> : <p>まだ準備が終わっていません。</p>}
          <Link className="button secondary" to="/calibration">設定をやり直す</Link>
        </section>
      </div>
      <div className="section-row"><h2>バックアップと出力</h2></div>
      <section className="panel data-actions">
        <div><span className="panel-label">すべての記録</span><h3>バックアップ</h3><p>設定とすべての記録を、1つのファイルに保存または復元します。</p><button className="button secondary" type="button" onClick={() => void exportData('json')}>バックアップを保存</button><button className="button ghost" type="button" onClick={() => inputRef.current?.click()}>バックアップから復元</button><input ref={inputRef} className="visually-hidden" type="file" accept="application/json,.json" onChange={importJson} /></div>
        <div><span className="panel-label">表計算ソフト用</span><h3>詳しいデータ</h3><p>1回ごとの問題と反応を、Excelなどで開ける形式で保存します。</p><button className="button secondary" type="button" onClick={() => void exportData('csv')}>詳しいデータを保存</button></div>
      </section>
      {pendingBackup && (
        <section className="panel" aria-labelledby="backup-preview-title">
          <div className="panel-label">復元する内容の確認</div>
          <h2 id="backup-preview-title">復元内容を確認</h2>
          <p><strong>{pendingBackup.filename}</strong><br />書き出し日時: {new Date(pendingBackup.preview.exportedAt).toLocaleString('ja-JP')}</p>
          <dl className="settings-definition">
            <div><dt>セッション</dt><dd>{pendingBackup.preview.sessionCount} 件</dd></div>
            <div><dt>1回ごとの回答記録</dt><dd>{pendingBackup.preview.trialCount} 件</dd></div>
            <div><dt>評価要約</dt><dd>{pendingBackup.preview.summaryCount} 件</dd></div>
            <div><dt>画面設定 / 使用環境</dt><dd>{pendingBackup.preview.calibrationCount} / {pendingBackup.preview.environmentCount} 件</dd></div>
          </dl>
          <Notice title="現在のデータを置き換えます。" tone="warning">必要な現在の記録があれば、先にバックアップを保存してください。</Notice>
          <button className="button danger" type="button" onClick={() => void confirmImport()}>置き換えて復元する</button>
          <button className="button ghost" type="button" onClick={() => setPendingBackup(undefined)}>キャンセル</button>
        </section>
      )}
      <div className="section-row"><h2>危険な操作</h2></div>
      <section className="panel danger-zone"><div className="panel-label">取り消せない操作</div><h2>このブラウザ内の全記録を削除</h2><p>画面設定、個人設定、実施記録、履歴を削除します。必要であれば先にバックアップを保存してください。</p><button className="button danger" type="button" onClick={clear}>すべて削除する</button></section>
    </div>
  );
}
